// components/skeletons/ProfileSkeleton.tsx
// Placeholder for the "My profile" page while the profile loads:
// header card (avatar, name + role, email, edit button) and the
// Personal Details / Agency cards with their info rows.

import React from 'react';
import { G, C } from '../GlobalComponents/uiTokens';
import { Bone, SkeletonStyles } from './Bone';

interface ProfileSkeletonProps {
  /** draw the Agency card next to Personal Details (default true) */
  showAgency?: boolean;
}

const SHELL: React.CSSProperties = {
  background: G.shellBg,
  border: `1px solid ${G.shellBorder}`,
  borderRadius: '13px',
  padding: '4.6px',
};
const INNER: React.CSSProperties = {
  background: C.white,
  border: `1px solid ${G.line}`,
  borderRadius: '9.2px',
};

/** section title: lime bar + icon tile + text */
const SectionHead: React.FC<{ w: number }> = ({ w }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '7.4px', marginBottom: '14.8px' }}>
    <Bone w={3} h={15} r={3} />
    <Bone w={27} h={27} r={7} />
    <Bone w={w} h={11} />
  </div>
);

/** icon tile + label + value, like InfoRow */
const Row: React.FC<{ last?: boolean; valueW: string }> = ({ last, valueW }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '9.2px', padding: '8.3px 0', borderBottom: last ? 'none' : `1px solid ${G.softer}` }}>
    <Bone w={26} h={26} r={7} />
    <Bone w={70} h={8} style={{ marginRight: '20px' }} />
    <Bone w={valueW} h={10} />
  </div>
);

const InfoCard: React.FC<{ title: number; values: string[] }> = ({ title, values }) => (
  <div style={SHELL}>
    <div style={{ ...INNER, padding: '16.6px 18.5px' }}>
      <SectionHead w={title} />
      {values.map((v, i) => (
        <Row key={i} valueW={v} last={i === values.length - 1} />
      ))}
    </div>
  </div>
);

const ProfileSkeleton: React.FC<ProfileSkeletonProps> = ({ showAgency = true }) => (
  <div role="status" aria-busy="true" aria-label="Loading profile" style={{ margin: '0 auto' }}>
    <SkeletonStyles />

    {/* header card */}
    <div style={{ ...SHELL, marginBottom: '11px' }}>
      <div style={{ ...INNER, padding: '18.5px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '13px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '13px' }}>
          <Bone w={54} h={54} r="50%" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '9.2px' }}>
              <Bone w={150} h={16} />
              <Bone w={52} h={18} r={20} />
            </div>
            <Bone w={190} h={10} />
          </div>
        </div>
        <Bone w={118} h={30} r={7} />
      </div>
    </div>

    {/* detail cards */}
    <div style={{ display: 'grid', gridTemplateColumns: showAgency ? '1fr 1fr' : '1fr', gap: '11px' }}>
      <InfoCard title={110} values={['70%', '55%', '35%', '65%', '45%']} />
      {showAgency && <InfoCard title={60} values={['40%', '65%', '45%', '50%', '30%']} />}
    </div>
  </div>
);

export default ProfileSkeleton;